// Step 13 — PackScene's capstone section. Sits below the items grid and
// drives the user toward the bonus full-scene panorama in three render
// branches:
//
//   locked            silhouette blur + "collect all to unlock" copy
//   unlocked-pending  lineart visible + "Color the panorama" CTA
//   completed         user's colored artwork + share icon
//
// We mount unconditionally so a kid sees the shape of the goal long before
// they unlock it (mirrors how the items grid shows dormant cells). The
// 1.91:1 aspect ratio matches the share-card target so the eventual share
// preview is a 1:1 visual carry-over.

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { type PackBonusResponse, type PackId, PACKS } from '@skena/schema'
import { fetchPackBonus } from './sceneApi.ts'

const LINEART_BLUR_PX = 10
const LINEART_GRAYSCALE = 0.6

export function CapstoneSection({ packId }: { packId: PackId }) {
  const query = useQuery({
    queryKey: ['pack-bonus', packId],
    queryFn: () => fetchPackBonus(packId),
    staleTime: 30 * 1000,
  })

  if (query.isLoading) {
    return (
      <div
        className="mt-6 animate-pulse overflow-hidden rounded-[var(--radius-soft)] bg-black/5"
        style={{ aspectRatio: '1200 / 630' }}
        aria-label="loading capstone"
      />
    )
  }
  if (query.isError || !query.data) {
    // Defensive: silent — the items grid still works without this section.
    return null
  }

  return <CapstoneCard packId={packId} bonus={query.data} />
}

function CapstoneCard({ packId, bonus }: { packId: PackId; bonus: PackBonusResponse }) {
  const pack = PACKS[packId]
  const totalItems = pack.itemIds.length

  if (bonus.isComplete && bonus.artworkUrl) {
    return <CompletedBranch packId={packId} bonus={bonus} artworkUrl={bonus.artworkUrl} />
  }
  if (bonus.isUnlocked) {
    return <UnlockedPendingBranch packId={packId} bonus={bonus} totalItems={totalItems} />
  }
  return <LockedBranch packId={packId} bonus={bonus} totalItems={totalItems} />
}

function LockedBranch({
  packId,
  bonus,
  totalItems,
}: {
  packId: PackId
  bonus: PackBonusResponse
  totalItems: number
}) {
  return (
    <section
      className="mt-6 overflow-hidden rounded-[var(--radius-soft)] border border-[var(--color-muted)]/15 bg-white/40 shadow-[var(--shadow-card)]"
      aria-label={`${PACKS[packId].nameZh} 全景大獎 — 還沒解鎖`}
    >
      <div className="relative overflow-hidden" style={{ aspectRatio: '1200 / 630' }}>
        <img
          src={bonus.lineartUrl}
          alt=""
          aria-hidden
          crossOrigin="anonymous"
          className="h-full w-full scale-110 object-cover opacity-70"
          style={{ filter: `blur(${LINEART_BLUR_PX}px) grayscale(${LINEART_GRAYSCALE})` }}
          onError={(e) => {
            ;(e.currentTarget as HTMLImageElement).style.display = 'none'
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/15">
          <p className="rounded-[var(--radius-pill)] bg-white/85 px-4 py-2 text-sm text-[var(--color-fg)] backdrop-blur">
            蒐齊全部 {totalItems} 件解鎖全景大獎
          </p>
        </div>
      </div>
    </section>
  )
}

function UnlockedPendingBranch({
  packId,
  bonus,
  totalItems,
}: {
  packId: PackId
  bonus: PackBonusResponse
  totalItems: number
}) {
  const ringClass =
    packId === 'joseph'
      ? 'ring-[var(--color-joseph-primary)]'
      : 'ring-[var(--color-nativity-primary)]'
  return (
    <section
      className="mt-6 overflow-hidden rounded-[var(--radius-soft)] border border-[#c89a44]/60 bg-white/70 shadow-[var(--shadow-card)]"
      aria-label={`${bonus.nameZh} — 來上色全景`}
    >
      <Link
        to={`/pack/${packId}/bonus`}
        className="block focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <div className="relative overflow-hidden" style={{ aspectRatio: '1200 / 630' }}>
          <img
            src={bonus.lineartUrl}
            alt={bonus.nameZh}
            crossOrigin="anonymous"
            className="h-full w-full object-cover"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).style.display = 'none'
            }}
          />
        </div>
        <div className="flex items-center justify-between gap-3 p-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-[#c89a44]">
              全景大獎 · {totalItems}/{totalItems}
            </p>
            <p className="truncate font-[family-name:var(--font-display)] text-xl text-[var(--color-fg)]">
              {bonus.nameZh}
            </p>
            <p className="text-xs text-[var(--color-muted)]">{bonus.scriptureRef}</p>
          </div>
          <span
            className={`shrink-0 rounded-[var(--radius-pill)] bg-[#c89a44] px-4 py-2 text-sm font-semibold text-[#3a2818] shadow-md ring-2 ring-offset-2 ring-offset-white ${ringClass}`}
          >
            ✦ 來上色全景
          </span>
        </div>
      </Link>
    </section>
  )
}

function CompletedBranch({
  packId,
  bonus,
  artworkUrl,
}: {
  packId: PackId
  bonus: PackBonusResponse
  artworkUrl: string
}) {
  const totalItems = PACKS[packId].itemIds.length
  return (
    <section
      className="mt-6 overflow-hidden rounded-[var(--radius-soft)] border border-[#c89a44]/60 bg-white/70 shadow-[var(--shadow-card)]"
      aria-label={`${bonus.nameZh} — 已完成`}
    >
      <div className="relative">
        <Link
          to={`/pack/${packId}/bonus`}
          className="block focus-visible:outline-2 focus-visible:outline-offset-2"
          aria-label={`${bonus.nameZh} — 繼續上色`}
        >
          <div className="overflow-hidden" style={{ aspectRatio: '1200 / 630' }}>
            <img
              src={artworkUrl}
              alt={bonus.nameZh}
              crossOrigin="anonymous"
              className="h-full w-full object-cover"
            />
          </div>
        </Link>
        <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/40 to-transparent p-3">
          <p className="text-xs uppercase tracking-wider text-[#f5d68a]">
            全景大獎 · {totalItems}/{totalItems}
          </p>
          <p className="font-[family-name:var(--font-display)] text-lg text-white drop-shadow">
            {bonus.nameZh}
          </p>
        </div>
      </div>
    </section>
  )
}
